import type {ReactElement, ReactNode} from 'react';
import React, {useEffect, useRef, useState} from 'react';
import {Button, Size, Type} from '@Components/button';
import {Dropdown} from '@Components/dropdown-v2';
import {ControlledListItemType7} from '@Components/controlled-list/components/controlled-list-item-type-7';
import type {AssistantMessageImagePartActionsRowProps} from '@Components/ai-chat-window/components/assistant-message-image-part-actions-row/assistant-message-image-part-actions-row.types';
import {MORE_OPTIONS_DROPDOWN_MODAL_PANEL_ID} from '@Components/ai-chat-window/components/assistant-message-image-part-actions-row/assistant-message-image-part-actions-row.types';
import styles from './assistant-message-image-part-actions-row.module.scss';
import {useAiChat} from '@/ai-chat/providers/ai-chat.context';
import {PublishingOptions} from '@Components/ai-chat-window/components/publishing-options/publishing-options';
import {PublishingOption} from '@Components/ai-chat-window/components/publishing-options/publishing-options.types';
import {useUpdateMoreOptionsModal} from '@Components/modal/hooks/useUpdateMoreOptionsModal';
import type {GeneratedImagePartActionsLoading} from '@/ai-chat/providers/ai-chat-provider.types';
import {LoadingStates} from '@Utils/loading.util';
import {SkeletonBrick} from '@Components/skeleton-brick';
import {useSaveToMyDesigns} from '@Components/ai-chat-window/hooks/useSaveToMyDesigns';
import {useResetAiActionLoadingStatesOnModalClose} from '@Components/ai-chat-window/hooks/useResetAiActionLoadingStatesOnModalClose';
import {usePublishChatModelGeneratedImage} from '@Components/ai-chat-window/hooks/usePublishChatModelGeneratedImage';
import {ALIGNMENT_TYPE} from '@Components/dropdown-v2/dropdown.types';
import {ChangeStyleDropdown} from '@Components/ai-chat-window/components/change-style-dropdown/change-style-dropdown';
import {useIsEventsCalendarPromotionChat} from '@Components/ai-chat-window/hooks/useIsEventsCalendarPromotionChat';
import {isUserPAYG} from '@Libraries/user.library';
import {AnimationCreationStatus} from '@Components/ai-chat-window/components/assistant-message-image-part-animation-creations/assistant-message-image-part-animation-creations.types';
import {AnimationGenerationErrorCode, ensureAnimationCredits, generateAnimationForAiChatImage} from '@Components/ai-chat-window/lib/ai-chat-animation-generation.library';
import type {AnimationGenerationError} from '@Components/ai-chat-window/lib/ai-chat-animation-generation.library';
import {AiChatTurnToVideoSource, AiChatTurnToVideoType, GA4EventName, GA4EventParamName} from '@Libraries/ga-events';
import {TurnIntoVideoTooltip} from './components/turn-into-video-tooltip/turn-into-video-tooltip';
import {hasUserSeenAiChatTurnIntoVideoTooltip, markAiChatTurnIntoVideoTooltipAsSeen} from './assistant-message-image-part-actions-row.library';
import {markAiChatCreateVideoIntroTileAsSeen} from '@Components/ai-chat-window/components/assistant-message-image-part-action-tiles/assistant-message-image-part-action-tiles.library';
import TooltipTrigger from '@Components/tooltip-trigger';
export function AssistantMessageImagePartActionsRow({...props}: AssistantMessageImagePartActionsRowProps): ReactElement {
  const {state, actions} = useAiChat();
  const {publishToSocialMedia, publishAsEmailCampaign, promoteAsEvent} = usePublishChatModelGeneratedImage(props.chatId, props.hashedId);
  const {onSaveToMyDesigns} = useSaveToMyDesigns(props.chatId, props.hashedId);
  const isEventsCalendarPromotionChat = useIsEventsCalendarPromotionChat(props.chatId);

  const getLoadingStatesForImageActions = (): GeneratedImagePartActionsLoading => {
    return state.getLoadingStatesForImageActions(props.chatId, props.hashedId);
  };

  useResetAiActionLoadingStatesOnModalClose(props.chatId, props.hashedId);

  const [isMoreOptionsOpen, setIsMoreOptionsOpen] = useState(false);
  const [showVideoTooltip, setShowVideoTooltip] = useState(false);
  const [hasSeenVideoTooltip, setHasSeenVideoTooltip] = useState(true);
  const turnIntoVideoRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const checkHasSeenVideoTooltip = async (): Promise<void> => {
      const hasSeen = await hasUserSeenAiChatTurnIntoVideoTooltip();
      setHasSeenVideoTooltip(hasSeen);
    };
    void checkHasSeenVideoTooltip();
  }, []);

  const handleMoreOptionsOpen = (): void => {
    setIsMoreOptionsOpen(true);
  };

  const handleMoreOptionsClose = (): void => {
    setIsMoreOptionsOpen(false);
  };

  const isDisabled =
    getLoadingStatesForImageActions().publishingLoadingState === LoadingStates.LOADING ||
    getLoadingStatesForImageActions().openEditorLoadingState === LoadingStates.LOADING ||
    getLoadingStatesForImageActions().shareLoadingState === LoadingStates.LOADING ||
    getLoadingStatesForImageActions().saveToMyDesignsLoadingState === LoadingStates.LOADING;

  const getEventsCalendarPromotionInlineActions = (): ReactNode => {
    if (!isEventsCalendarPromotionChat || !props.isLastMessage) {
      return null;
    }

    if (props.isMessageStreaming) {
      return (
        <>
          <SkeletonBrick width={120} height={32} className="radius-8" />
          <SkeletonBrick width={120} height={32} className="radius-8" />
        </>
      );
    }

    const isPublishingDisabled = getLoadingStatesForImageActions().publishingLoadingState === LoadingStates.LOADING;

    return (
      <>
        <Button
          customClasses="radius-8"
          type={Type.SECONDARY}
          size={Size.XSMALL}
          icon="icon-social-media"
          text={window.i18next.t('pmwjs_post_on_social_media')}
          onClick={(): void => {
            void publishToSocialMedia();
          }}
          disabled={!props.canWrite || isPublishingDisabled}
        />
        <Button
          customClasses="radius-8"
          type={Type.SECONDARY}
          size={Size.XSMALL}
          icon={isUserPAYG() ? 'icon-crown' : 'icon-envelope'}
          text={window.i18next.t('pmwjs_send_as_email')}
          onClick={(): void => {
            void publishAsEmailCampaign();
          }}
          disabled={!props.canWrite || isPublishingDisabled}
        />
      </>
    );
  };

  const getChangeStyleDropdown = (): ReactNode => {
    if (!props.isLastMessage) {
      return null;
    }

    if (props.isMessageStreaming && props.isLastMessage) {
      return <SkeletonBrick width={120} height={32} className="radius-8" />;
    }

    return (
      <ChangeStyleDropdown
        chatId={props.chatId}
        onGenerate={props.onGenerate}
        imageHashedId={props.hashedId}
        canWrite={props.canWrite}
        imageWidth={props.imageWidth}
        imageHeight={props.imageHeight}
      />
    );
  };

  const getTurnIntoVideoPill = (): ReactNode => {
    if (!state.isAiChatCreateAVideoFeatureEnabled) {
      return null;
    }
    if (!props.isLastMessage) {
      return null;
    }

    if (props.isMessageStreaming) {
      return <SkeletonBrick width={state.isSmall ? 32 : 80} height={32} className="radius-8" />;
    }

    const handleTurnIntoVideoClick = (): void => {
      if (!props.canWrite) {
        return;
      }

      void (async (): Promise<void> => {
        const generateAudio = state.getAnimationCreationState(props.chatId, props.hashedId).generateAudio ?? true;
        const hasCredits = await ensureAnimationCredits(generateAudio);
        if (!hasCredits) {
          return;
        }

        window.PMW.gtm.trackGA4CustomEvent(GA4EventName.AICHAT_TURNTOVIDEO, {
          [GA4EventParamName.SOURCE]: AiChatTurnToVideoSource.DESIGN,
          [GA4EventParamName.TYPE]: generateAudio ? AiChatTurnToVideoType.WITH_AUDIO : AiChatTurnToVideoType.SILENT,
        });

        const abortController = new AbortController();

        actions.updateActionTileStates({
          isCreateAVideoFeatureIntroVisible: false,
        });
        void markAiChatCreateVideoIntroTileAsSeen();
        actions.updateAnimationCreationState(props.chatId, props.hashedId, {
          isVisible: true,
          status: AnimationCreationStatus.CREATING,
          abortController,
          generateAudio,
        });

        try {
          const animation = await generateAnimationForAiChatImage(props.hashedId, {
            signal: abortController.signal,
            onPollingKeyReceived: (pollingKey: string): void => {
              actions.updateAnimationCreationState(props.chatId, props.hashedId, {pollingKey});
            },
            generateAudio,
          });
          actions.updateAnimationCreationState(props.chatId, props.hashedId, {
            status: AnimationCreationStatus.COMPLETED,
            animations: [animation],
            abortController: undefined,
            pollingKey: undefined,
          });
          actions.refreshCredits?.();
        } catch (e) {
          if ((e as AnimationGenerationError).code === AnimationGenerationErrorCode.INSUFFICIENT_CREDITS) {
            actions.updateAnimationCreationState(props.chatId, props.hashedId, {
              isVisible: false,
              status: AnimationCreationStatus.IDLE,
              abortController: undefined,
              pollingKey: undefined,
            });
            const hasPurchasedCredits = await ensureAnimationCredits(generateAudio);
            if (hasPurchasedCredits) {
              handleTurnIntoVideoClick();
            }
            return;
          }

          if (!abortController.signal.aborted) {
            actions.updateAnimationCreationState(props.chatId, props.hashedId, {
              status: AnimationCreationStatus.FAILED,
              errorMessage: window.i18next.t('pmwjs_animation_creation_failed'),
              abortController: undefined,
              pollingKey: undefined,
            });
          }
        }
      })();
    };

    const isGeneratingVideo = state.getAnimationCreationState(props.chatId, props.hashedId).status === AnimationCreationStatus.CREATING;

    const handleVideoMouseEnter = (): void => {
      if (!hasSeenVideoTooltip) {
        setShowVideoTooltip(true);
        void markAiChatTurnIntoVideoTooltipAsSeen();
        setHasSeenVideoTooltip(true);
      }
    };

    const handleVideoMouseLeave = (): void => {
      setShowVideoTooltip(false);
    };

    const getVideoTooltip = (): ReactElement | null => {
      if (!showVideoTooltip || state.isSmall) {
        return null;
      }

      return (
        <TurnIntoVideoTooltip
          referenceElementRef={turnIntoVideoRef}
          onClose={(): void => {
            setShowVideoTooltip(false);
          }}
        />
      );
    };

    const buttonNode = (
      <div ref={turnIntoVideoRef} onMouseEnter={handleVideoMouseEnter} onMouseLeave={handleVideoMouseLeave}>
        <Button
          textClasses={styles.noWrap}
          customClasses={`radius-8 _full-height ${styles.turnIntoVideoButton}`}
          type={Type.SECONDARY}
          size={Size.XSMALL}
          text={window.i18next.t('pmwjs_turn_into_video')}
          icon={'icon-video-ai'}
          onClick={handleTurnIntoVideoClick}
          disabled={!props.canWrite || isGeneratingVideo}
        />
        {getVideoTooltip()}
      </div>
    );

    if (isGeneratingVideo) {
      return (
        <TooltipTrigger
          text={window.i18next.t('pmwjs_unavailable_while_generating')}
          overlayClass={styles.turnIntoVideoTooltipWrapper}
          tooltipClass={styles.turnIntoVideoTooltip}
        >
          {buttonNode}
        </TooltipTrigger>
      );
    }

    return buttonNode;
  };

  const truncatedMoreOptionsDropdownPopup = (): ReactElement => {
    return (
      <>
        <ControlledListItemType7
          id={'open-image-in-editor'}
          text={window.i18next.t('pmwjs_edit_image_in_editor')}
          icon={'icon-pencil'}
          onClick={() => {
            void props.onOpenPosterInEditor();
          }}
          isLoading={getLoadingStatesForImageActions().openEditorLoadingState === LoadingStates.LOADING}
          isDisabled={isDisabled}
          className={'radius-8'}
        />
        <ControlledListItemType7
          id={'download-option'}
          text={window.i18next.t('pmwjs_download')}
          icon={'icon-download'}
          onClick={() => {
            props.onDownload();
          }}
          isLoading={getLoadingStatesForImageActions().downloadLoadingState === LoadingStates.LOADING}
          isDisabled={getLoadingStatesForImageActions().downloadLoadingState === LoadingStates.LOADING}
          className={'radius-8'}
        />
        <PublishingOptions
          chatId={props.chatId}
          hashedId={props.hashedId}
          loadingStates={getLoadingStatesForImageActions()}
          publishToSocialMedia={publishToSocialMedia}
          publishAsEmailCampaign={publishAsEmailCampaign}
          promoteAsEvent={promoteAsEvent}
          removeLastItemBottomMargin={false}
          excludedOptions={isEventsCalendarPromotionChat ? [PublishingOption.PUBLISH_TO_SOCIAL_MEDIA, PublishingOption.PUBLISH_AS_EMAIL_CAMPAIGN] : []}
        />
        <ControlledListItemType7
          id={'save-to-my-designs'}
          text={window.i18next.t('pmwjs_save_to_my_designs')}
          icon={'icon-folder-2'}
          onClick={() => {
            void onSaveToMyDesigns();
          }}
          isLoading={getLoadingStatesForImageActions().saveToMyDesignsLoadingState === LoadingStates.LOADING}
          isDisabled={isDisabled}
          className={'radius-8'}
        />
        <ControlledListItemType7
          id={'share-design-option'}
          text={window.i18next.t('pmwjs_share_with_others')}
          icon={'icon-group-add'}
          onClick={() => {
            void props.onShareDesign();
          }}
          isLoading={getLoadingStatesForImageActions().shareLoadingState === LoadingStates.LOADING}
          isDisabled={isDisabled}
          className={'radius-8'}
        />
      </>
    );
  };

  const getTruncatedMoreOptions = (): ReactNode => {
    if (!props.isLastMessage) {
      return null;
    }

    if (props.isMessageStreaming) {
      return (
        <>
          <SkeletonBrick width={32} height={32} className="radius-8" />
        </>
      );
    }

    return (
      <Dropdown
        open={isMoreOptionsOpen}
        onOpen={handleMoreOptionsOpen}
        onClose={handleMoreOptionsClose}
        isSmallScreenModalBottomSheet
        doCloseOnInnerClick={true}
        showModalForSmallScreen
        modalTitle={window.i18next.t('pmwjs_more_options')}
        panelIdForModal={MORE_OPTIONS_DROPDOWN_MODAL_PANEL_ID}
        alignment={ALIGNMENT_TYPE.BOTTOM_START}
        popupClassName={styles.actionPopup}
        className={styles.positionUnset}
        selector={
          <Button
            disabled={!props.canWrite}
            customClasses={`radius-8 _full-height ${styles.moreOptionsButton} `}
            type={Type.SECONDARY}
            size={Size.XSMALL}
            icon={'icon-more-h'}
            isActive={isMoreOptionsOpen}
          />
        }
        popup={truncatedMoreOptionsDropdownPopup()}
      />
    );
  };

  useUpdateMoreOptionsModal(
    truncatedMoreOptionsDropdownPopup(),
    [
      isDisabled,
      getLoadingStatesForImageActions().downloadLoadingState,
      getLoadingStatesForImageActions().openEditorLoadingState,
      getLoadingStatesForImageActions().shareLoadingState,
      getLoadingStatesForImageActions().saveToMyDesignsLoadingState,
      getLoadingStatesForImageActions().loadingPublishingOption,
    ],
    MORE_OPTIONS_DROPDOWN_MODAL_PANEL_ID
  );

  return (
    <div className={`${styles.suggestedPillsSection} _position-relative`}>
      {getChangeStyleDropdown()}
      {getEventsCalendarPromotionInlineActions()}
      {getTurnIntoVideoPill()}
      {getTruncatedMoreOptions()}
    </div>
  );
}
